import { forwardRef, useEffect, useMemo, useState } from "react";
import { apiService } from "../../services/api";
import creditService, { creditLineLabel } from "../../services/creditService.js";
import { formatCurrency } from "../../utils/formatters";
import { storeLicenseEntries } from "../../utils/licenseMarkets.js";

// The shop's copy of the admin portal's Order PDF
// (admin-portal-2.1/src/features/orders/orders/details/OrderPDF.jsx). The two must print the
// same invoice: keep everything from formattedData down identical, and change only how the data
// is loaded here — the shop reads its own self-scoped endpoints instead of the admin OData.

// Same as the admin's sort (orders/utils/groupOrderItems byLabel): case-insensitive and
// number-aware, whitespace ignored, so 50MG < 100MG < 1000MG.
const sortKey = (label) => (label || "").replace(/\s+/g, "");
const byLabel = (a, b) => sortKey(a).localeCompare(sortKey(b), undefined, { sensitivity: "base", numeric: true });

// The admin's $expand minus OrderTransactions — the shop loads payments and credits from
// /storefront/orders/{id}/payments and /credits, which return only what the invoice shows.
const ORDER_EXPAND = `Brand,clientSalesRepUser,brandAmbassadorUser,client($expand=state,clientLicenses,Company($select=ID,Name,DisplayName,DBAName,Address,City,StateID,Zip,Phone,InvoiceFooterText;$expand=State($select=ID,Code,Name),CompanyLicenses($select=ID,Number,Expiration,OptLicenseTypeID,OptLicensesID;$expand=OptLicenses($select=ID,Name,State,ListOrder),OptLicenseType($select=ID,Name)),OptLicenses($filter=OptStatusID eq 1)),route($expand=routeDays($expand=optDay)),optPaymentType,optPaymentTerm,clientSalesRepUser,clientBrandAmbassadorUser),orderItems($expand=productSKUVariant($expand=brand,productSKU,manufacturer($expand=manufacturerLicenses($expand=OptLicenses($select=ID,Name),OptLicenseType($select=ID,Name)))),productBatchLot($expand=productBatch)),optPaymentStatus,optFulfillmentStatus,optDeliveryStatus,orderBrandSalesReps($expand=salesRepUser($select=firstName,lastName,fullName))`;

// OptOrderTransactionTypes: a client credit applied to the order (ClientCreditService.ApplyAsync).
const ORDER_TRANSACTION_TYPE_CREDIT = 5;

const OrderPDF = forwardRef(({ orderId, orderData: orderDataProp, mergeInfo: mergeInfoProp, onReady }, ref) => {

  // Internal fetch state (used when orderId is provided without orderData)
  const [fetchedOrderData, setFetchedOrderData] = useState(null);
  const [fetchedMergeInfo, setFetchedMergeInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Resolve active data: prefer props, fall back to fetched
  const orderData = orderDataProp || fetchedOrderData;
  const mergeInfo = mergeInfoProp ?? fetchedMergeInfo;

  // Fetch data when orderId is provided without orderData
  useEffect(() => {
    if (!orderId || orderDataProp) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      apiService.get(`/storefront-odata/StorefrontOrders(${orderId})`, { $expand: ORDER_EXPAND }),
      apiService.get(`/storefront/orders/${orderId}/merge-info`).catch(() => null),
      // Credits and payments are fetched here, before onReady fires, so a print always includes them.
      creditService.getOrderCredits(orderId),
      apiService.get(`/storefront/orders/${orderId}/payments`).catch(() => []),
    ])
      .then(([data, merge, credits, payments]) => {
        if (cancelled) return;
        // The same shape the public invoice hands the admin PDF: credit lines ready-made, and the
        // order's transactions as orderTransactions.
        setFetchedOrderData({
          ...data,
          creditLines: Array.isArray(credits) ? credits : [],
          orderTransactions: Array.isArray(payments) ? payments : [],
        });
        setFetchedMergeInfo(merge);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("OrderPDF: Failed to fetch order data", err);
        setError(err);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [orderId, orderDataProp]);

  // Calculate totals and format data
  const formattedData = useMemo(() => {
    if (!orderData) {
      return null;
    }

    // Calculate line item totals
    const lineItems =
      orderData.orderItems?.map((item) => {
        const qty = parseInt(item.unitQuantity) || 0;
        const isSample = item.isSample || false;

        const unitPrice = parseFloat(item.unitPrice) || 0;
        const singleUnitFee = isSample ? 0 : (parseFloat(item.singleUnitFee) || 0);

        const lineItemSubtotal = qty * unitPrice;
        const lineItemTotal = qty * (unitPrice + singleUnitFee);

        // Get item number: legacySKU or productSKUVariantID
        const itemNo =
          item.productSKUVariant?.legacySKU ||
          item.productSKUVariant?.productSKU?.legacySKU ||
          item.productSKUVariantID?.toString() ||
          "";

        // Get batch info
        const batchName = item.productBatchLot?.productBatch?.batchName || "";
        const rfid = item.productBatchLot?.rfid || "";

        return {
          id: item.id,
          itemNo: itemNo,
          description: `${item.productSKUVariant?.name || "Unknown Product"}${item.productSKUVariant?.flavor ? " - " + item.productSKUVariant.flavor : ""}`,
          brand: item.productSKUVariant?.brand?.name || "",
          manufacturerID: item.productSKUVariant?.manufacturerID,
          manufacturer: item.productSKUVariant?.manufacturer,
          batchName: batchName,
          rfid: rfid,
          qty: qty,
          unitPrice: unitPrice,
          singleUnitFee: singleUnitFee,
          lineItemSubtotal: lineItemSubtotal,
          lineItemTotal: lineItemTotal,
          isSample: isSample,
        };

      }) || [];

    // Separate regular items and samples
    const regularItems = lineItems.filter((item) => !item.isSample);
    const sampleItems = lineItems.filter((item) => item.isSample);

    // Sort items by brand, then product (name + flavor) — the order the order modal shows — then,
    // within one product, batch and lot (RFID)
    const sortItems = (items) => {
      return [...items].sort((a, b) => {
        // First sort by brand
        const brandCompare = byLabel(a.brand, b.brand);
        if (brandCompare !== 0) return brandCompare;

        // Then by product name + flavor
        const productCompare = byLabel(a.description, b.description);
        if (productCompare !== 0) return productCompare;

        // Then by batch name
        const batchCompare = (a.batchName || "").localeCompare(b.batchName || "");
        if (batchCompare !== 0) return batchCompare;

        // Finally by RFID (lot)
        return (a.rfid || "").localeCompare(b.rfid || "");
      });
    };

    const sortedRegularItems = sortItems(regularItems);
    const sortedSampleItems = sortItems(sampleItems);

    // Extract unique manufacturer licenses from all items.
    // The license name comes from each manufacturer license's own OptLicense, with its type in
    // parentheses when it has one, e.g. "DEA (Manufacturer)" vs "DEA (Distributor)".
    const manufacturerLicensesMap = new Map();
    [...sortedRegularItems, ...sortedSampleItems].forEach((item) => {
      if (item.manufacturer?.manufacturerLicenses) {
        item.manufacturer.manufacturerLicenses.forEach((license) => {
          const key = `${item.manufacturerID}-${license.id}`;
          if (!manufacturerLicensesMap.has(key)) {
            const labelName = license.optLicenses?.name || "";
            const typeName = license.optLicenseType?.name;
            manufacturerLicensesMap.set(key, {
              manufacturerID: item.manufacturerID,
              manufacturerName: item.manufacturer?.name || "",
              licenseName: typeName ? `${labelName} (${typeName})` : labelName,
              licenseNumber: license.number || "",
              licenseExpiration: license.expiration,
            });
          }
        });
      }
    });

    const manufacturerLicenses = Array.from(manufacturerLicensesMap.values()).sort(
      (a, b) => (a.manufacturerID || 0) - (b.manufacturerID || 0)
    );

    // Calculate subtotal (only regular items)
    const subtotal = sortedRegularItems.reduce(
      (sum, item) => sum + item.lineItemSubtotal,
      0,
    );

    // Calculate sample total
    const sampleTotal = sortedSampleItems.reduce(
      (sum, item) => sum + item.lineItemTotal,
      0,
    );

    // Get costs from order (assuming these might be added later)
    const singleUnitFees = sortedRegularItems.reduce(
      (sum, item) => sum + item.qty * item.singleUnitFee,
      0,
    );
    const taxRate = parseFloat(orderData.taxRate) || 0;
    const shippingHandling = parseFloat(orderData.shippingHandling) || 0;
    const additionalCosts = parseFloat(orderData.additionalCosts) || 0;
    const refundAmount = parseFloat(orderData.refundAmount) || 0;
    // Client credits replace refunds, so the invoice shows them the same way: taken off the total,
    // not paid. Each credit is its own line (the brand, Stash House or Overpay). They post as type-5
    // "Credit" transactions, so the payments below leave those out.
    const isCreditTransaction = (t) => t.orderTransactionTypeID === ORDER_TRANSACTION_TYPE_CREDIT;
    const creditLines = (orderData.creditLines || []).map((line) => ({
      label: creditLineLabel(line),
      amount: parseFloat(line.amount) || 0,
    }));
    const creditAmount = creditLines.reduce((sum, line) => sum + line.amount, 0);

    // Calculate tax amount
    const totalTax = (subtotal * taxRate) / 100;

    // Calculate grand total (subtract refund and credits)
    const total =
      subtotal + sampleTotal + totalTax + shippingHandling + additionalCosts + singleUnitFees - refundAmount - creditAmount;

    // Format dates
    const formatDate = (dateString) => {
      if (!dateString) return "";
      // Parse date string to avoid timezone offset issues
      const dateStr = dateString.split('T')[0];
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    };

    const formatShortDate = (dateString) => {
      if (!dateString) return "";
      // Parse date string to avoid timezone offset issues
      // For date-only strings like "2025-01-15", split and create date with local timezone
      const dateStr = dateString.split('T')[0];
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    };

    // ============================================================================
    // PAYMENTS (EN-111)
    // Payments applied to this order (OrderTransaction rows), oldest → newest.
    // Followed on the invoice by a BALANCE DUE = TOTAL − sum(payments).
    // ============================================================================
    const payments = (orderData.orderTransactions || [])
      .filter((t) => !isCreditTransaction(t))
      .map((t) => ({
        id: t.id,
        amount: parseFloat(t.amount) || 0,
        appliedDate: t.appliedDate,
        dateLabel: formatShortDate(t.appliedDate),
        typeName: t.orderTransactionType?.name || "",
        checkNumber: t.checkNumber || "",
      }))
      .sort((a, b) => (a.appliedDate || "").localeCompare(b.appliedDate || ""));

    const paymentsTotal = payments.reduce((sum, p) => sum + p.amount, 0);
    const balanceDue = total - paymentsTotal;

    // ============================================================================
    // COMPANY INFO WITH LICENSE LOOKUP
    // Find the license matching client's state with lowest ListOrder
    // ============================================================================
    const company = orderData.client?.company;
    const clientStateId = orderData.client?.stateID;

    // Filter licenses by client's state and sort by ListOrder to get the one with lowest order
    const matchingLicense = (company?.companyLicenses || [])
      .filter((cl) => cl.optLicenses?.state === clientStateId)
      .sort(
        (a, b) =>
          (a.optLicenses?.listOrder ?? 999) - (b.optLicenses?.listOrder ?? 999),
      )[0];

    // ============================================================================
    // CUSTOMER LICENSE INFO
    // Get all customer licenses and look up their names from company.optLicenses
    // Filter by CompanyID matching the current company
    // ============================================================================
    const companyID = orderData.client?.company?.id;
    const optLicenses = orderData.client?.company?.optLicenses || [];

    // EN-196: a split Medical / Recreational invoice shows that market's licenses, and a label
    // the store holds in both markets is named with its market ("M.E.D. – Recreational").
    const customerLicenses = storeLicenseEntries(
      (orderData.client?.clientLicenses || []).filter(license => license.companyID === companyID),
      (labelId) => optLicenses.find(opt => opt.id === labelId)?.name,
      { invoiceMarket: orderData.optInvoiceTypeID },
    ).filter(license => license.label && license.number);

    return {
      id: orderData.id,
      optInvoiceTypeID: orderData.optInvoiceTypeID, // 1 = Med, 2 = Rec, null = not a split order
      brandName: orderData.brand?.name || null, // Brand name for brand-specific invoices
      dateCreated: formatDate(orderData.dateCreated),
      dateCreatedShort: formatShortDate(orderData.dateCreated),
      requestedDeliveryDate: formatShortDate(orderData.requestedDeliveryDate),
      requestedDeliveryTime: orderData.requestedDeliveryTime || "",
      specialNotes: orderData.specialNotes || "",
      stashHouseNotes: orderData.stashHouseNotes || "",

      // Client info (Ship To / Bill To)
      client: {
        name: orderData.client?.name || "",
        address: orderData.client?.address || "",
        city: `${orderData.client?.city || ""}, ${orderData.client?.state?.code || ""} ${orderData.client?.zip || ""}`.trim(),
        phone: orderData.client?.phone || orderData.client?.cellPhone || "",
        email: orderData.client?.email || "",
        invoiceNotes: orderData.client?.invoiceNotes || "",
        licenses: customerLicenses,
      },

      // Company info (From) - with license info
      company: {
        name: company?.name || "",
        displayName: company?.displayName || company?.name || "",
        dbaName: company?.dbaName || "",
        address: company?.address || "",
        city: `${company?.city || ""}, ${company?.state?.code || ""} ${company?.zip || ""}`.trim(),
        phone: company?.phone || "",
        // License info - from the matching license for the client's state
        licenseNumber: matchingLicense?.number || "",
        licenseName: matchingLicense?.optLicenses?.name || "",
        licenseType: matchingLicense?.optLicenseType?.name || "",
        licenseExpiration: matchingLicense?.expiration
          ? formatShortDate(matchingLicense.expiration)
          : "",
        invoiceFooterText: company?.invoiceFooterText || "",
      },

      regularItems: sortedRegularItems,
      sampleItems: sortedSampleItems,
      manufacturerLicenses,
      payments,

      totals: {
        subtotal: subtotal,
        sampleTotal: sampleTotal,
        taxRate: taxRate,
        totalTax: totalTax,
        shippingHandling: shippingHandling,
        additionalCosts: additionalCosts,
        singleUnitFees: singleUnitFees,
        refundAmount: refundAmount,
        creditAmount: creditAmount,
        creditLines: creditLines,
        total: total,
        paymentsTotal: paymentsTotal,
        balanceDue: balanceDue,
      },

      // Status info
      paymentStatus:
        orderData.optPaymentStatus?.name ||
        orderData.optPaymentStatus?.displayName ||
        "Unpaid",
      fulfillmentStatus:
        orderData.optFulfillmentStatus?.name ||
        orderData.optFulfillmentStatus?.displayName ||
        "Pending",
      deliveryStatus:
        orderData.optDeliveryStatus?.name ||
        orderData.optDeliveryStatus?.displayName ||
        "Pending",

      // Rep info - Get first name and last name (fallback to client's sales rep if order's is null)
      salesRepFirstName: orderData.clientSalesRepUser?.firstName || orderData.client?.clientSalesRepUser?.firstName || "",
      salesRepLastName: orderData.clientSalesRepUser?.lastName || orderData.client?.clientSalesRepUser?.lastName || "",
      salesRepFullName:
        orderData.clientSalesRepUser?.fullName ||
        orderData.client?.clientSalesRepUser?.fullName ||
        "",

      // Brand invoices only: the brand's reps for this store as recorded when the order was placed
      // (OrderBrandSalesReps, EN-180). Orders placed before that recorded none and print "N/A".
      brandSalesRepNames: (orderData.orderBrandSalesReps || [])
        .map(({ salesRepUser: u }) =>
          u?.firstName && u?.lastName ? `${u.firstName} ${u.lastName}` : u?.fullName || "")
        .filter(Boolean)
        .sort(byLabel)
        .join(", "),
    };
  }, [orderData]);

  // Notify caller when data is loaded and rendered
  useEffect(() => {
    if (formattedData && !loading && onReady) {
      // Use setTimeout(0) to ensure DOM has painted before callback
      const timer = setTimeout(() => onReady(), 0);
      return () => clearTimeout(timer);
    }
  }, [formattedData, loading, onReady]);

  // Loading state (only when self-fetching)
  if (loading) {
    return (
      <div ref={ref} style={{ padding: "20px", textAlign: "center" }}>
        <p>Loading order data...</p>
      </div>
    );
  }

  // Error state (only when self-fetching)
  if (error) {
    return (
      <div ref={ref} style={{ padding: "20px", textAlign: "center" }}>
        <p>Failed to load order data</p>
      </div>
    );
  }

  // Don't render if no data
  if (!formattedData) {
    return (
      <div ref={ref} style={{ padding: "20px", textAlign: "center" }}>
        <p>No order data available</p>
      </div>
    );
  }

  const data = formattedData;

const numToAlpha =(n) =>{
    if (n <= 0) return 'N/A';
    
    let result = '';
    while (n > 0) {
        const mod = (n - 1) % 26;
        result = String.fromCharCode(65 + mod) + result;
        n = Math.floor((n - 1) / 26);
    }
    return result;
}
  return (
    <div
      ref={ref}
      className="order-pdf"
      style={{
        width: "8.5in",
        minHeight: "11in",
        margin: "0 auto",
        padding: "0.5in",
        backgroundColor: "white",
        fontFamily: "Arial, sans-serif",
        fontSize: "12px",
        lineHeight: "1.4",
        color: "#000",
      }}
    >
      {/* Print styles to remove browser header/footer */}
      <style>
        {`
          @media print {
            @page {
              margin: 0.5in;
            }
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
        `}
      </style>

      {/* Header Section */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          {/* Company Logo */}
          <div style={{ marginRight: "20px" }}>
            <img
              src="/images/logo-dark.png"
              alt="Company Logo"
              style={{
                width: "150px",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
        </div>
      </div>

      {/* Order Title */}
      <div
        style={{
          backgroundColor: "#E5E5E5",
          padding: "15px",
          textAlign: "center",
          marginBottom: "20px",
          border: "1px solid #ccc",
        }}
      >
        <h2 style={{ margin: "0", fontSize: "24px", fontWeight: "bold" }}>
          {data.optInvoiceTypeID === 1 && "MED "}
          {data.optInvoiceTypeID === 2 && "REC "}
          {data.brandName && `${data.brandName} `}
          INVOICE #{data.id}
        </h2>
      </div>

      {/* From, Bill To, and Ship To Section */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div style={{ width: "64%" }}>
          <h3
            style={{
              margin: "0 0 10px 0",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            FROM
          </h3>
          <div style={{ fontSize: "12px" }}>
            <p style={{ margin: "2px 0", fontWeight: "bold" }}>
              {data.company.name}
            </p>

            <p style={{ margin: "2px 0" }}>
              {data.company.licenseNumber && (
                <>License: {data.company.licenseNumber} </>
              )}
              {data.company.licenseExpiration && (
                <>Expiration: {data.company.licenseExpiration}</>
              )}
            </p>
            {data.company.address && (
              <p style={{ margin: "2px 0" }}>{data.company.address}</p>
            )}
            {data.company.city && (
              <p style={{ margin: "2px 0" }}>{data.company.city}</p>
            )}
            {data.company.phone && (
              <p style={{ margin: "2px 0" }}>Phone: {data.company.phone}</p>
            )}
          </div>
        </div>
        <div style={{ display: "none" }}>
          <h3
            style={{
              margin: "0 0 10px 0",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            BILL TO
          </h3>
          <div style={{ fontSize: "12px" }}>
            <p style={{ margin: "2px 0", fontWeight: "bold" }}>
              {data.client.name}
            </p>
            <p style={{ margin: "2px 0" }}>{data.client.address}</p>
            <p style={{ margin: "2px 0" }}>{data.client.city}</p>
            <p style={{ margin: "2px 0" }}>{data.client.phone}</p>
            {data.client.email && (
              <p style={{ margin: "2px 0" }}>{data.client.email}</p>
            )}
          </div>
        </div>
        <div style={{ width: "32%", textAlign: "end" }}>
          <h3
            style={{
              margin: "0 0 10px 0",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            DELIVER TO
          </h3>
          <div style={{ fontSize: "12px" }}>
            <p style={{ margin: "2px 0", fontWeight: "bold" }}>
              {data.client.name}
            </p>
            <p style={{ margin: "2px 0" }}>{data.client.address}</p>
            <p style={{ margin: "2px 0" }}>{data.client.city}</p>
            <p style={{ margin: "2px 0" }}>{data.client.phone}</p>
            {data.client.email && (
              <p style={{ margin: "2px 0" }}>{data.client.email}</p>
            )}
            {data.client.licenses.map((license) => (
              license.label && license.number && (
                <p key={license.number} style={{ margin: "2px 0" }}>
                  {license.label}: <span style={{ fontWeight: "bold" }}>{license.number}</span>
                </p>
              )
            ))}
          </div>
        </div>
      </div>

      {/* Notes Section - Full Width */}
      {(data.specialNotes || data.stashHouseNotes || data.client.invoiceNotes) && (
        <div
          style={{
            marginBottom: "20px",
            pageBreakInside: "avoid",
            breakInside: "avoid",
          }}
        >
          {data.specialNotes && (
            <div
              style={{
                border: "1px solid #ccc",
                padding: "10px",
                marginBottom: (data.stashHouseNotes || data.client.invoiceNotes) ? "10px" : "0",
              }}
            >
              <p
                style={{
                  margin: "0 0 5px 0",
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                Customer Notes:
              </p>
              <p
                style={{
                  margin: "0",
                  fontSize: "11px",
                  whiteSpace: "pre-wrap",
                }}
              >
                {data.specialNotes}
              </p>
            </div>
          )}
          {data.stashHouseNotes && (
            <div
              style={{
                border: "1px solid #ccc",
                padding: "10px",
                marginBottom: data.client.invoiceNotes ? "10px" : "0",
              }}
            >
              <p
                style={{
                  margin: "0 0 5px 0",
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                Stash House Notes:
              </p>
              <p
                style={{
                  margin: "0",
                  fontSize: "11px",
                  whiteSpace: "pre-wrap",
                }}
              >
                {data.stashHouseNotes}
              </p>
            </div>
          )}
          {data.client.invoiceNotes && (
            <div
              style={{
                border: "1px solid #ccc",
                padding: "10px",
              }}
            >
              <p
                style={{
                  margin: "0 0 5px 0",
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                Delivery Notes:
              </p>
              <p
                style={{
                  margin: "0",
                  fontSize: "11px",
                  whiteSpace: "pre-wrap",
                }}
              >
                {data.client.invoiceNotes}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Order Info and Status */}
      <table
        style={{
          width: "100%",
          marginBottom: "20px",
          borderCollapse: "collapse",
        }}
      >
        <thead>
          <tr style={{ backgroundColor: "#666", color: "white" }}>
            <th
              style={{
                padding: "8px",
                textAlign: "center",
                fontSize: "11px",
                border: "1px solid #ccc",
              }}
            >
              DELIVERY DATE
            </th>

            <th
              style={{
                padding: "8px",
                textAlign: "center",
                fontSize: "11px",
                border: "1px solid #ccc",
              }}
            >
              PAYMENT STATUS
            </th>
            <th
              style={{
                padding: "8px",
                textAlign: "center",
                fontSize: "11px",
                border: "1px solid #ccc",
              }}
            >
              SALES REP
            </th>
            {data.brandName && (
              <th
                style={{
                  padding: "8px",
                  textAlign: "center",
                  fontSize: "11px",
                  border: "1px solid #ccc",
                }}
              >
                {data.brandName.toUpperCase()} SALES REP
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              style={{
                padding: "8px",
                textAlign: "center",
                fontSize: "11px",
                border: "1px solid #ccc",
              }}
            >
              {data.requestedDeliveryDate}
            </td>

            <td
              style={{
                padding: "8px",
                textAlign: "center",
                fontSize: "11px",
                border: "1px solid #ccc",
              }}
            >
              {data.paymentStatus}
            </td>
            <td
              style={{
                padding: "8px",
                textAlign: "center",
                fontSize: "11px",
                border: "1px solid #ccc",
              }}
            >
              {data.salesRepFirstName && data.salesRepLastName
                ? `${data.salesRepFirstName} ${data.salesRepLastName}`
                : data.salesRepFullName || "N/A"}
            </td>
            {data.brandName && (
              <td
                style={{
                  padding: "8px",
                  textAlign: "center",
                  fontSize: "11px",
                  border: "1px solid #ccc",
                }}
              >
                {data.brandSalesRepNames || "N/A"}
              </td>
            )}
          </tr>
        </tbody>
      </table>

      {/* Merge Info - displayed in place of order items when order was merged */}
      {mergeInfo?.isSourceOrder && (
        <div
          style={{
            backgroundColor: "#f0f0f0",
            border: "1px solid #ccc",
            padding: "20px",
            marginBottom: "20px",
            fontSize: "12px",
            color: "#333",
            textAlign: "center",
          }}
        >
          <strong>Order Merged:</strong>{" "}
          This order was merged
          {mergeInfo.otherSourceOrderIds && mergeInfo.otherSourceOrderIds.length > 0 && (
            <> with order{mergeInfo.otherSourceOrderIds.length > 1 ? "s" : ""} #{mergeInfo.otherSourceOrderIds.join(", #")}</>
          )}
          {" "}to create order #{mergeInfo.mergedOrderId}.
        </div>
      )}

      {/* Regular Line Items Table */}
      {!mergeInfo?.isSourceOrder && data.regularItems.length > 0 && (
        <>
          <h3
            style={{
              margin: "20px 0 10px 0",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            ORDER ITEMS
          </h3>
          <table
            style={{
              width: "100%",
              marginBottom: "20px",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#666", color: "white" }}>
                <th
                    style={{
                    padding: "8px",
                    textAlign: "left",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                    width: "5%",
                  }}
                ></th>
                <th
                  style={{
                    padding: "8px",
                    textAlign: "left",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                    width: "12%",
                  }}
                >
                  ITEM NO.
                </th>
                <th
                  style={{
                    padding: "8px",
                    textAlign: "left",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                    width: "15%",
                  }}
                >
                  BRAND
                </th>
                <th
                  style={{
                    padding: "8px",
                    textAlign: "left",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                    width: "35%",
                  }}
                >
                  DESCRIPTION
                </th>
                <th
                  style={{
                    padding: "8px",
                    textAlign: "center",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                    width: "13%",
                  }}
                >
                  QTY
                </th>
                <th
                  style={{
                    padding: "8px",
                    textAlign: "right",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                    width: "12%",
                  }}
                >
                  UNIT PRICE
                </th>
                <th
                  style={{
                    padding: "8px",
                    textAlign: "right",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                    width: "13%",
                  }}
                >
                  TOTAL
                </th>
              </tr>
            </thead>
            <tbody>
              {data.regularItems.map((item, index) => (
                <tr
                  key={item.id ?? index}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#f9f9f9" : "white",
                  }}
                >
                <td
                    style={{
                      padding: "8px",
                      fontSize: "11px",
                      border: "1px solid #ccc",
                }}>{numToAlpha(item.manufacturerID)}</td>
                  <td
                    style={{
                      padding: "8px",
                      fontSize: "11px",
                      border: "1px solid #ccc",
                    }}
                  >
                    {item.itemNo}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      fontSize: "11px",
                      border: "1px solid #ccc",
                    }}
                  >
                    {item.brand}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      fontSize: "11px",
                      border: "1px solid #ccc",
                    }}
                  >
                    <div>{item.description}</div>
                    {(item.batchName || item.rfid) && (
                      <div
                        style={{
                          fontSize: "10px",
                          color: "#666",
                          marginTop: "4px",
                        }}
                      >
                        {item.batchName && <div>Batch: {item.batchName}</div>}
                        {item.rfid && <div>RFID: {item.rfid}</div>}
                      </div>
                    )}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "center",
                      fontSize: "11px",
                      border: "1px solid #ccc",
                    }}
                  >
                    {item.qty}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      fontSize: "11px",
                      border: "1px solid #ccc",
                    }}
                  >
                    {formatCurrency(item.unitPrice)}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      fontSize: "11px",
                      border: "1px solid #ccc",
                    }}
                  >
                    {formatCurrency(item.lineItemTotal)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* Sample Items Table */}
      {!mergeInfo?.isSourceOrder && data.sampleItems.length > 0 && (
        <>
          <h3
            style={{
              margin: "20px 0 10px 0",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            SAMPLE ITEMS
          </h3>
          <table
            style={{
              width: "100%",
              marginBottom: "20px",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#666", color: "white" }}>
                <th
                    style={{
                    padding: "8px",
                    textAlign: "left",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                    width: "5%",
                  }}
                ></th>
                <th
                  style={{
                    padding: "8px",
                    textAlign: "left",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                    width: "12%",
                  }}
                >
                  ITEM NO.
                </th>
                <th
                  style={{
                    padding: "8px",
                    textAlign: "left",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                    width: "15%",
                  }}
                >
                  BRAND
                </th>
                <th
                  style={{
                    padding: "8px",
                    textAlign: "left",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                    width: "35%",
                  }}
                >
                  DESCRIPTION
                </th>
                <th
                  style={{
                    padding: "8px",
                    textAlign: "center",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                    width: "13%",
                  }}
                >
                  QTY
                </th>
                <th
                  style={{
                    padding: "8px",
                    textAlign: "right",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                    width: "12%",
                  }}
                >
                  UNIT PRICE
                </th>
                <th
                  style={{
                    padding: "8px",
                    textAlign: "right",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                    width: "13%",
                  }}
                >
                  TOTAL
                </th>
              </tr>
            </thead>
            <tbody>
              {data.sampleItems.map((item, index) => (
                <tr
                  key={item.id ?? index}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#f9f9f9" : "white",
                  }}
                >
                <td
                    style={{
                      padding: "8px",
                      fontSize: "11px",
                      border: "1px solid #ccc",
                }}>{numToAlpha(item.manufacturerID)}</td>
                  <td
                    style={{
                      padding: "8px",
                      fontSize: "11px",
                      border: "1px solid #ccc",
                    }}
                  >
                    {item.itemNo}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      fontSize: "11px",
                      border: "1px solid #ccc",
                    }}
                  >
                    {item.brand}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      fontSize: "11px",
                      border: "1px solid #ccc",
                    }}
                  >
                    <div>{item.description}</div>
                    {(item.batchName || item.rfid) && (
                      <div
                        style={{
                          fontSize: "10px",
                          color: "#666",
                          marginTop: "4px",
                        }}
                      >
                        {item.batchName && <div>Batch: {item.batchName}</div>}
                        {item.rfid && <div>RFID: {item.rfid}</div>}
                      </div>
                    )}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "center",
                      fontSize: "11px",
                      border: "1px solid #ccc",
                    }}
                  >
                    {item.qty}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      fontSize: "11px",
                      border: "1px solid #ccc",
                    }}
                  >
                    {formatCurrency(item.unitPrice)}
                  </td>
                  <td
                    style={{
                      padding: "8px",
                      textAlign: "right",
                      fontSize: "11px",
                      border: "1px solid #ccc",
                    }}
                  >
                    {formatCurrency(item.lineItemTotal)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}


      {/* Bottom Section with Totals - Keep together on same page */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "30px",
          pageBreakInside: "avoid",
          breakInside: "avoid",
        }}
      >
        <div style={{ width: "64%" }}>
          <div>
            
            {data.company.invoiceFooterText && (
              <div
                style={{
                  margin: "0",
                  fontSize: "11px",
                  whiteSpace: "pre-wrap",
                }}
                dangerouslySetInnerHTML={{ __html: data.company.invoiceFooterText }}
 
              >
               
              </div>
            )}
          </div>
        </div>
        <div style={{ width: "32%" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                <td
                  style={{
                    padding: "5px 10px",
                    textAlign: "left",
                    fontSize: "11px",
                    backgroundColor: "#f0f0f0",
                  }}
                >
                  SUBTOTAL
                </td>
                <td
                  style={{
                    padding: "5px 10px",
                    textAlign: "right",
                    fontSize: "11px",
                    backgroundColor: "#f0f0f0",
                  }}
                >
                  {formatCurrency(data.totals.subtotal)}
                </td>
              </tr>
              {data.totals.sampleTotal > 0 && (
                <tr>
                  <td
                    style={{
                      padding: "5px 10px",
                      textAlign: "left",
                      fontSize: "11px",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    SAMPLE TOTAL
                  </td>
                  <td
                    style={{
                      padding: "5px 10px",
                      textAlign: "right",
                      fontSize: "11px",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    {formatCurrency(data.totals.sampleTotal)}
                  </td>
                </tr>
              )}
              {data.totals.taxRate > 0 && (
                <tr>
                  <td
                    style={{
                      padding: "5px 10px",
                      textAlign: "left",
                      fontSize: "11px",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    TAX ({data.totals.taxRate}%)
                  </td>
                  <td
                    style={{
                      padding: "5px 10px",
                      textAlign: "right",
                      fontSize: "11px",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    {formatCurrency(data.totals.totalTax)}
                  </td>
                </tr>
              )}
              {data.totals.singleUnitFees > 0 && (
                <tr>
                  <td
                    style={{
                      padding: "5px 10px",
                      textAlign: "left",
                      fontSize: "11px",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    UNIT FEE
                  </td>
                  <td
                    style={{
                      padding: "5px 10px",
                      textAlign: "right",
                      fontSize: "11px",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    {formatCurrency(data.totals.singleUnitFees)}
                  </td>
                </tr>
              )}

              <tr>
                <td
                  style={{
                    padding: "5px 10px",
                    textAlign: "left",
                    fontSize: "11px",
                    backgroundColor: "#f0f0f0",
                  }}
                >
                  DELIVERY FEE
                </td>
                <td
                  style={{
                    padding: "5px 10px",
                    textAlign: "right",
                    fontSize: "11px",
                    backgroundColor: "#f0f0f0",
                  }}
                >
                  {data.totals.shippingHandling > 0 ? (
                    <>{formatCurrency(data.totals.shippingHandling)}</>
                  ) : (
                    <>FREE</>
                  )}
                </td>
              </tr>
              {data.totals.refundAmount > 0 && (
                <tr>
                  <td
                    style={{
                      padding: "5px 10px",
                      textAlign: "left",
                      fontSize: "11px",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    REFUND
                  </td>
                  <td
                    style={{
                      padding: "5px 10px",
                      textAlign: "right",
                      fontSize: "11px",
                      backgroundColor: "#f0f0f0",
                      color: "#dc3545",
                      fontWeight: "bold",
                    }}
                  >
                    ({formatCurrency(data.totals.refundAmount)})
                  </td>
                </tr>
              )}
              {/* Credits: a heading, then one indented line per credit applied (Stash House or the brand) */}
              {data.totals.creditLines.length > 0 && (
                <>
                  <tr>
                    <td
                      style={{
                        padding: "5px 10px",
                        textAlign: "left",
                        fontSize: "11px",
                        backgroundColor: "#f0f0f0",
                      }}
                    >
                      CREDITS
                    </td>
                    <td style={{ padding: "5px 10px", backgroundColor: "#f0f0f0" }}></td>
                  </tr>
                  {data.totals.creditLines.map((line, i) => (
                    <tr key={`credit-${i}`}>
                      <td
                        style={{
                          padding: "3px 10px 3px 24px",
                          textAlign: "left",
                          fontSize: "11px",
                          backgroundColor: "#f0f0f0",
                        }}
                      >
                        {line.label}
                      </td>
                      <td
                        style={{
                          padding: "3px 10px",
                          textAlign: "right",
                          fontSize: "11px",
                          backgroundColor: "#f0f0f0",
                          color: "#dc3545",
                          fontWeight: "bold",
                        }}
                      >
                        ({formatCurrency(line.amount)})
                      </td>
                    </tr>
                  ))}
                </>
              )}
              {data.totals.additionalCosts > 0 && (
                <tr>
                  <td
                    style={{
                      padding: "5px 10px",
                      textAlign: "left",
                      fontSize: "11px",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    OTHER
                  </td>
                  <td
                    style={{
                      padding: "5px 10px",
                      textAlign: "right",
                      fontSize: "11px",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    {formatCurrency(data.totals.additionalCosts)}
                  </td>
                </tr>
              )}
              <tr>
                <td
                  style={{
                    padding: "8px 10px",
                    textAlign: "left",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#000000",
                    backgroundColor: "#666",
                  }}
                >
                  TOTAL
                </td>
                <td
                  style={{
                    padding: "8px 10px",
                    textAlign: "right",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#000000",
                    backgroundColor: "#666",
                  }}
                >
                  {formatCurrency(data.totals.total)}
                </td>
              </tr>

              {/* Payments applied + Balance Due (EN-111) */}
              {data.payments.length > 0 && (
                <>
                  <tr>
                    <td
                      style={{
                        padding: "5px 10px",
                        textAlign: "left",
                        fontSize: "11px",
                        backgroundColor: "#f0f0f0",
                      }}
                    >
                      PAYMENTS
                    </td>
                    <td
                      style={{
                        padding: "5px 10px",
                        textAlign: "right",
                        fontSize: "11px",
                        backgroundColor: "#f0f0f0",
                      }}
                    ></td>
                  </tr>
                  {data.payments.map((payment) => (
                    <tr key={payment.id}>
                      <td
                        style={{
                          padding: "5px 10px",
                          textAlign: "left",
                          fontSize: "11px",
                          backgroundColor: "#f0f0f0",
                        }}
                      >
                        {payment.dateLabel}
                        {(payment.typeName || payment.checkNumber) && (
                          <> — {payment.typeName}{payment.checkNumber ? ` #${payment.checkNumber}` : ""}</>
                        )}
                      </td>
                      <td
                        style={{
                          padding: "5px 10px",
                          textAlign: "right",
                          fontSize: "11px",
                          backgroundColor: "#f0f0f0",
                        }}
                      >
                        {formatCurrency(payment.amount)}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td
                      style={{
                        padding: "8px 10px",
                        textAlign: "left",
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: "#000000",
                        backgroundColor: "#f0f0f0",
                      }}
                    >
                      BALANCE DUE
                    </td>
                    <td
                      style={{
                        padding: "8px 10px",
                        textAlign: "right",
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: "#000000",
                        backgroundColor: "#f0f0f0",
                      }}
                    >
                      {formatCurrency(data.totals.balanceDue)}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Manufacturer Licenses Table */}
      {data.manufacturerLicenses.length > 0 && (
        <table
          style={{
            width: "100%",
            marginTop: "20px",
            marginBottom: "20px",
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#666", color: "white" }}>
              <th
                style={{
                  padding: "8px",
                  textAlign: "left",
                  fontSize: "11px",
                  border: "1px solid #ccc",
                  width: "5%",
                }}
              ></th>
              <th
                style={{
                  padding: "8px",
                  textAlign: "left",
                  fontSize: "11px",
                  border: "1px solid #ccc",
                  width: "25%",
                }}
              >
                MANUFACTURER NAME
              </th>
              <th
                style={{
                  padding: "8px",
                  textAlign: "left",
                  fontSize: "11px",
                  border: "1px solid #ccc",
                  width: "20%",
                }}
              >
                LICENSE NAME
              </th>
              <th
                style={{
                  padding: "8px",
                  textAlign: "left",
                  fontSize: "11px",
                  border: "1px solid #ccc",
                  width: "30%",
                }}
              >
                LICENSE NUMBER
              </th>
              <th
                style={{
                  padding: "8px",
                  textAlign: "center",
                  fontSize: "11px",
                  border: "1px solid #ccc",
                  width: "20%",
                }}
              >
                EXPIRATION
              </th>
            </tr>
          </thead>
          <tbody>
            {data.manufacturerLicenses.map((license, index) => (
              <tr
                key={`${license.manufacturerID}-${license.licenseNumber}`}
                style={{
                  backgroundColor: index % 2 === 0 ? "#f9f9f9" : "white",
                }}
              >
                <td
                  style={{
                    padding: "8px",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                  }}
                >
                  {numToAlpha(license.manufacturerID)}
                </td>
                <td
                  style={{
                    padding: "8px",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                  }}
                >
                  {license.manufacturerName}
                </td>
                <td
                  style={{
                    padding: "8px",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                  }}
                >
                  {license.licenseName}
                </td>
                <td
                  style={{
                    padding: "8px",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                  }}
                >
                  {license.licenseNumber}
                </td>
                <td
                  style={{
                    padding: "8px",
                    textAlign: "center",
                    fontSize: "11px",
                    border: "1px solid #ccc",
                  }}
                >
                  {license.licenseExpiration
                    ? (() => {
                        // Parse date string to avoid timezone offset issues
                        const dateStr = license.licenseExpiration.split('T')[0];
                        return new Date(dateStr + 'T00:00:00').toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                        });
                      })()
                    : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Signature Section - Keep with totals */}
      <div
        style={{
          marginBottom: "30px",
          pageBreakInside: "avoid",
          breakInside: "avoid",
          marginTop: "40px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "40px",
          }}
        >
          {/* Driver Signature */}
          <div style={{ flex: 1 }}>
            <div
              style={{
                borderBottom: "1px solid #000",
                marginBottom: "8px",
                minHeight: "40px",
              }}
            >
              &nbsp;
            </div>
            <p style={{ margin: "0", fontSize: "11px", textAlign: "center", fontWeight: "bold" }}>
              DRIVER SIGNATURE
            </p>
          </div>

          {/* Customer Signature */}
          <div style={{ flex: 1 }}>
            <div
              style={{
                borderBottom: "1px solid #000",
                marginBottom: "8px",
                minHeight: "40px",
              }}
            >
              &nbsp;
            </div>
            <p style={{ margin: "0", fontSize: "11px", textAlign: "center", fontWeight: "bold" }}>
              CUSTOMER SIGNATURE
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

OrderPDF.displayName = "OrderPDF";

export default OrderPDF;